// LiveStreams.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';

import getOperationData from '../selector/getOperationData.js';

import setActiveStreamId from '../action/setActiveStreamId.js';

import { Swiper, SwiperSlide } from '../component/Swiper.jsx';

import ModalView from '../container/ModalView.js';
import LivestreamMiniModal from '../container/LivestreamMiniModal.js';

import withRouter from '../component/WithRouter.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import ModalTracking from '../component/ModalTracking.jsx';

import { Game, GIOCO_GAMES } from '../resource/gameConstants.js';
import {
  LIVESTREAM_PATH,
  PRESET_PREVIEW,
} from '../resource/liveStreamConstants.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { ModalId } from '../resource/mixpanel.js';
import {
  iPhoneXSeriesFooterPaddingInPWA,
  breakpoint,
  sicBoHeight,
  sicBoZIndex,
  dragonTigerFightHeight,
  dragonTigerFightHeaderHeight,
  dragonTigerFightZIndex,
  giftSlotHeight,
  giftSlotHeaderHeight,
  giftSlotZIndex,
} from '../style/variables.js';

const TOOLTIP_DELAY_MSEC = 5000; // TODO: remote config
const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();

class LiveStreams extends React.PureComponent {
  state = {
    isServer: true,
  };
  swiperRef = React.createRef();
  timeout = undefined;
  tooltipTimeout = null;
  feedTimeout = null;
  constructor(props) {
    super(props);
    const {
      streamId: userId,
      username,
      fetchUser,
      staticContext,
      fetchUserDetailError,
    } = props;
    if (staticContext.actions) {
      if (fetchUserDetailError) {
        staticContext.error = fetchUserDetailError;
      }
      if (userId && !username) {
        staticContext.actions.push({
          function: fetchUser,
          argumentObject: {
            userId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
    }
  }
  fetchFeeds = ({ page }) => {
    const { feedNameWithQuery, fetchFeeds } = this.props;

    if (feedNameWithQuery && page !== null) {
      clearTimeout(this.feedTimeout);
      this.feedTimeout = setTimeout(() => {
        fetchFeeds({
          type: feedNameWithQuery,
          page,
          limit: 20,
        });
      }, 0);
    }
  };
  shouldFetchNextFeeds() {
    const { streamId, streamIds, nextPage } = this.props;
    const lastStream = streamIds[streamIds.length - 1];
    return (
      nextPage && (lastStream === streamId || !streamIds.includes(streamId))
    );
  }
  slideToIndex = ({ index }) => {
    if (this.swiperRef.current && !this.swiperRef.current.destroyed) {
      this.swiperRef.current.slideTo(index, 0, false);
    }
  };
  componentDidMount() {
    const {
      streamId,
      isStreamIdLiveStreaming,
      isStreamIdHasKaraoke,
      shouldUseInstantCommandTooltip,
      checkShouldDisplayKaraokeTooltip,
      hasFeedFetched,
      nextPage,
      setActiveStreamId,
    } = this.props;

    this.setState({
      isServer: false,
    });

    // Fetch initial feeds if not fetched before
    if (!hasFeedFetched) {
      this.fetchFeeds({ page: 1 });
    }

    // Fetch next page feeds if needed
    if (this.shouldFetchNextFeeds()) {
      this.fetchFeeds({ page: nextPage });
    }

    if (isStreamIdLiveStreaming && isStreamIdHasKaraoke) {
      this.tooltipTimeout = setTimeout(
        () => {
          checkShouldDisplayKaraokeTooltip();
        },
        shouldUseInstantCommandTooltip ? 0 : TOOLTIP_DELAY_MSEC
      );
    }

    setActiveStreamId({
      activeStreamId: streamId,
    });
  }
  componentDidUpdate(prevProps) {
    const {
      nextPage,
      streamId,
      streamIds,
      isStreamIdLiveStreaming,
      isStreamIdHasKaraoke,
      shouldUseInstantCommandTooltip,
      checkShouldDisplayKaraokeTooltip,
      feedNameWithQuery,
      setActiveStreamId,
    } = this.props;

    const {
      streamId: prevStreamId,
      streamIds: prevStreamIds,
      feedNameWithQuery: prevFeedNameWithQuery,
    } = prevProps;
    if (streamId !== prevStreamId) {
      setActiveStreamId({ activeStreamId: streamId });
      this.slideToIndex({ index: streamIds.indexOf(streamId) });
    }
    if (streamIds !== prevStreamIds) {
      if (this.swiperRef.current && !this.swiperRef.current.destroyed) {
        this.swiperRef.current.update();
        this.slideToIndex({ index: streamIds.indexOf(streamId) });
      }
    }
    if (streamId !== prevStreamId || streamIds !== prevStreamIds) {
      if (this.shouldFetchNextFeeds()) {
        this.fetchFeeds({ page: nextPage });
      }
    }

    if (feedNameWithQuery !== prevFeedNameWithQuery) {
      this.fetchFeeds({ page: 1 });
    }

    if (
      streamId !== prevStreamId ||
      isStreamIdLiveStreaming !== prevProps.isStreamIdLiveStreaming ||
      isStreamIdHasKaraoke !== prevProps.isStreamIdHasKaraoke
    ) {
      clearTimeout(this.tooltipTimeout);
      if (isStreamIdLiveStreaming && isStreamIdHasKaraoke) {
        this.tooltipTimeout = setTimeout(
          () => {
            checkShouldDisplayKaraokeTooltip();
          },
          shouldUseInstantCommandTooltip ? 0 : TOOLTIP_DELAY_MSEC
        );
      }
    }
  }
  componentWillUnmount() {
    const { leaveLiveStreams } = this.props;
    clearTimeout(this.tooltipTimeout);
    clearTimeout(this.feedTimeout);
    leaveLiveStreams();
  }

  render() {
    const {
      usingGameId,
      deviceHeight,
      deviceWidth,
      streamId: activeStreamId,
      isGiftPanelOpen,
      isGamePanelOpen,
      isGameOpen,
      isChatPanelOpen,
      isTriggerPrivatePanelOpen,
      isChatroomScrolling,
      isExclusivePrivateRelatedToMe,
      isPrivateStreamUnlocked,
      hasGroupPrivateStarted,
      streamIds,
      fetchedLivestreamIds,
      closeGameSicBo,
      setActiveStreamId,
      preset,
      replace,
    } = this.props;

    const isOnMobile = deviceWidth <= breakpoint.tablet;
    const isGameSicBoOpen = isGameOpen && Game.SIC_BO === usingGameId;
    const isGameGiocoOpen = isGameOpen && GIOCO_GAMES.includes(usingGameId);
    const isGameGiftSlotOpen = isGameOpen && Game.GIFT_SLOT === usingGameId;
    const giocoMixpanelModalId =
      usingGameId === Game.SOARING_HIGH
        ? ModalId.LiveStream.SoaringHigh
        : usingGameId === Game.GOLDEN_GATE
          ? ModalId.LiveStream.GoldenGate
          : ModalId.LiveStream.DragonTigerFight;

    // User can't swipe to other stream when:
    //   - Game panel is open
    //   - Gift panel is open
    //   - Chat panel is open
    //   - Trigger private panel is open
    //   - Chatroom is scrolling
    //   - Exclusive private is related to me
    const shouldDisableSwiper =
      isGamePanelOpen ||
      isGiftPanelOpen ||
      isChatPanelOpen ||
      isTriggerPrivatePanelOpen ||
      isChatroomScrolling ||
      isExclusivePrivateRelatedToMe;

    const currentIndex = streamIds.indexOf(activeStreamId);
    const canRenderSwiper = streamIds.length > 1;

    return (
      <>
        <StyledLiveStreams deviceHeight={deviceHeight}>
          {canRenderSwiper ? (
            <StyledSwiper
              direction={'vertical'}
              initialSlide={currentIndex}
              slidesPerView={1}
              spaceBetween={0}
              speed={400}
              touchRatio={1.2}
              simulateTouch={false}
              onSwiper={swiper => {
                this.swiperRef.current = swiper;
              }}
              onDestroy={() => {
                this.swiperRef.current = null;
              }}
              onTransitionEnd={({ activeIndex: nextActiveIndex }) => {
                const currentStreamId = activeStreamId;
                const nextStreamId = streamIds[nextActiveIndex];
                // Only update active stream id and clear previous active stream data if active stream id changed.
                if (currentStreamId === nextStreamId) return;

                // When reload page, swiper will trigger extra `onTransitionEnd()`
                // with activeIndex=0 on switching from no swiper to with swiper.
                if (
                  nextActiveIndex === 0 &&
                  !fetchedLivestreamIds.includes(currentStreamId)
                ) {
                  return;
                }
                setActiveStreamId({ activeStreamId: nextStreamId });

                // Replace URL triggers route change and component remount:
                // - Causes LiveStreams component to remount and trigger componentDidMount
                replace({
                  pathname: `/user/${nextStreamId}/${LIVESTREAM_PATH}`,
                  search: window.location.search,
                });
              }}
            >
              {streamIds.map((streamId, index) => (
                <SwiperSlide
                  key={streamId}
                  data-stream_id={streamId}
                  data-index={index}
                  data-active_stream_id={activeStreamId}
                >
                  <LiveStreamWrapper
                    className={shouldDisableSwiper ? 'swiper-no-swiping' : ''}
                  >
                    <UniversalComponent
                      filename="Container_LiveStream"
                      userId={streamId}
                      streamId={streamId}
                      isActive={streamId === activeStreamId}
                      isNearBy={Math.abs(currentIndex - index) <= 1}
                      importer={
                        /* #__LOADABLE__ */ () =>
                          import('../container/LiveStream.js')
                      }
                    />
                  </LiveStreamWrapper>
                </SwiperSlide>
              ))}
            </StyledSwiper>
          ) : (
            <UniversalComponent
              filename="Container_LiveStream"
              userId={activeStreamId}
              streamId={activeStreamId}
              isActive={true}
              importer={
                /* #__LOADABLE__ */ () => import('../container/LiveStream.js')
              }
            />
          )}
          <ModalView
            id="LiveStreamKaraokeModal"
            render={() => (
              <UniversalComponent
                filename="Container_LiveStreamKaraokeModal"
                modalId="LiveStreamKaraokeModal"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LiveStreamKaraokeModal.js')
                }
              />
            )}
          />
          <ModalView
            id="LiveStreamTriggerPrivateModal"
            modalId={ModalId.LiveStream.ModalLivestreamUserTrigger}
            render={() => (
              <UniversalComponent
                filename="Container_LiveStreamTriggerPrivateModal"
                modalId="LiveStreamTriggerPrivateModal"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LiveStreamTriggerPrivateModal.js')
                }
              />
            )}
          />
          <ModalView
            id="LiveStreamGiftModal"
            modalId={ModalId.LiveStream.ModalLivestreamGift}
            noScrollLock
            render={() => (
              <UniversalComponent
                filename="Container_LiveStreamGiftModal"
                modalId="LiveStreamGiftModal"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LiveStreamGiftModal.js')
                }
              />
            )}
          />
          <ModalView
            id="LivestreamGiftSendConfirmModal"
            render={() => (
              <UniversalComponent
                filename="Container_LivestreamGiftSendConfirmModal"
                modalId="LivestreamGiftSendConfirmModal"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LivestreamGiftSendConfirmModal.js')
                }
              />
            )}
          />
          <ModalView
            id="LeaveLivestreamToUserProfileConfirmModal"
            render={() => (
              <UniversalComponent
                filename="Container_LeaveLivestreamConfirmModal"
                streamId={activeStreamId}
                isGoToProfile={true}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LeaveLivestreamConfirmModal.js')
                }
              />
            )}
          />
          <ModalView
            id="LeaveLivestreamToListConfirmModal"
            modalId={
              isPrivateStreamUnlocked &&
              preset === PRESET_PREVIEW &&
              hasGroupPrivateStarted
                ? ModalId.LiveStream.ModalTriggerPrivateLeavingAlert
                : undefined
            }
            render={() => (
              <UniversalComponent
                filename="Container_LeaveLivestreamConfirmModal"
                streamId={activeStreamId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LeaveLivestreamConfirmModal.js')
                }
              />
            )}
          />
          <ModalView
            id="PrivateShowBuyTicketModal"
            modalId={ModalId.LiveStream.LivestreamShowPurchaseModal}
            render={() => (
              <UniversalComponent
                filename="Container_PrivateShowBuyTicketModal"
                streamId={activeStreamId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/PrivateShowBuyTicketModal.js')
                }
              />
            )}
          />
          <ModalView
            id="LiveStreamPrivateShowPopup"
            render={() => (
              <UniversalComponent
                filename="Container_LiveStreamPrivateShowPopup"
                streamId={activeStreamId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LiveStreamPrivateShowPopup.js')
                }
              />
            )}
          />
          <ModalView
            id="LivestreamBannedNotification"
            modalId={ModalId.LiveStream.LivestreamBannedNotification}
            render={() => (
              <UniversalComponent
                filename="Container_LivestreamBannedNotification"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LivestreamBannedNotification.js')
                }
              />
            )}
          />
          {isOnMobile && (
            <GameSicBoWrapper isOpen={isGameSicBoOpen}>
              {isGameSicBoOpen && (
                <ModalTracking
                  id="GameSicBo"
                  modalId={ModalId.LiveStream.SicBoGame}
                >
                  <UniversalComponent
                    filename="Container_GameSicBo"
                    closeGameSicBo={closeGameSicBo}
                    importer={
                      /* #__LOADABLE__ */ () =>
                        import('../container/GameSicBo.js')
                    }
                  />
                </ModalTracking>
              )}
            </GameSicBoWrapper>
          )}
          {isOnMobile && (
            <GameDragonTigerFightWrapper isOpen={isGameGiocoOpen}>
              {isGameGiocoOpen && (
                <ModalTracking
                  id="GameGiocoGroup"
                  modalId={giocoMixpanelModalId}
                >
                  <UniversalComponent
                    filename="Container_GameGiocoGroup"
                    streamId={activeStreamId}
                    mixpanelModalId={giocoMixpanelModalId}
                    importer={
                      /* #__LOADABLE__ */ () =>
                        import('../container/GameGiocoGroup.js')
                    }
                  />
                </ModalTracking>
              )}
            </GameDragonTigerFightWrapper>
          )}
          {isOnMobile && (
            <GameGiftSlotWrapper isOpen={isGameGiftSlotOpen}>
              {isGameGiftSlotOpen && (
                <ModalTracking
                  id="GameGiftSlot"
                  modalId={ModalId.LiveStream.GiftSlot}
                >
                  <UniversalComponent
                    filename="Container_GameGiftSlot"
                    streamId={activeStreamId}
                    importer={
                      /* #__LOADABLE__ */ () =>
                        import('../container/GameGiftSlot.js')
                    }
                  />
                </ModalTracking>
              )}
            </GameGiftSlotWrapper>
          )}
          {isOnMobile && (
            <ModalView
              id="GameMenuModal"
              modalId={ModalId.LiveStream.ModalLivestreamGameMenu}
            >
              <UniversalComponent
                filename="Component_GameMenuModal"
                modalId="GameMenuModal"
                mixpanelModalId={ModalId.LiveStream.ModalLivestreamGameMenu}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../component/GameMenuModal.jsx')
                }
              />
            </ModalView>
          )}
          {isOnMobile && (
            <ModalView
              id="MiniProfileModal"
              modalId={ModalId.LiveStream.LivestreamMiniProfile}
            >
              <LivestreamMiniModal
                modalId="MiniProfileModal"
                mixpanelModalId={ModalId.LiveStream.LivestreamMiniProfile}
              >
                <UniversalComponent
                  filename="Container_MiniProfile"
                  modalId="MiniProfileModal"
                  miniBadgeInfoModalId="MiniBadgeInfoModal"
                  mixpanelModalId={ModalId.LiveStream.LivestreamMiniProfile}
                  hasClosableBackground
                  importer={
                    /* #__LOADABLE__ */ () =>
                      import('../container/MiniProfile.js')
                  }
                />
              </LivestreamMiniModal>
            </ModalView>
          )}
          <ModalView
            id="MiniBadgeInfoModal"
            modalId={ModalId.LiveStream.MiniProfileBadgeDetail}
            noScrollLock
          >
            <UniversalComponent
              filename="Container_MiniBadgeInfo"
              modalId="MiniBadgeInfoModal"
              mixpanelModalId={ModalId.LiveStream.MiniProfileBadgeDetail}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/MiniBadgeInfo.js')
              }
            />
          </ModalView>
          <ModalView
            id="ProfileMoreModal"
            modalId={ModalId.Popup.ModalProfileMore}
            render={() => (
              <UniversalComponent
                filename="Container_ProfileMoreModal"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/ProfileMoreModal.js')
                }
              />
            )}
          />
          <ModalView
            id="LivestreamCommandProgressModal"
            noScrollLock
            render={() => (
              <UniversalComponent
                filename="Component_LivestreamCommandProgressModal"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../component/LivestreamCommandProgressModal.jsx')
                }
                modalId="LivestreamCommandProgressModal"
              />
            )}
          />
          {/* user using gesture navigation might use browser back/forward cache to restore the browser state.

          the active stream id might be empty/incorrect.

          so use interval to ensure the active stream id in store is synced. */}
          <EnsureActiveStreamId activeStreamId={activeStreamId} />
        </StyledLiveStreams>
      </>
    );
  }
}

LiveStreams.propTypes = {
  usingGameId: PropTypes.string,
  streamId: PropTypes.string,
  staticContext: PropTypes.object,
  fetchUserDetailError: PropTypes.instanceOf(Error),
  username: PropTypes.string,
  fetchUser: PropTypes.func,
  feedNameWithQuery: PropTypes.string,
  deviceHeight: PropTypes.number,
  deviceWidth: PropTypes.number,
  hasFeedFetched: PropTypes.bool,
  nextPage: PropTypes.number,
  fetchFeeds: PropTypes.func,
  shouldUseInstantCommandTooltip: PropTypes.bool,
  isGiftPanelOpen: PropTypes.bool,
  isChatPanelOpen: PropTypes.bool,
  isTriggerPrivatePanelOpen: PropTypes.bool,
  isGamePanelOpen: PropTypes.bool,
  isGameOpen: PropTypes.bool,
  isChatroomScrolling: PropTypes.bool,
  isPrivateStreamUnlocked: PropTypes.bool,
  isStreamIdLiveStreaming: PropTypes.bool,
  isExclusivePrivateRelatedToMe: PropTypes.bool,
  isStreamIdHasKaraoke: PropTypes.bool,
  hasGroupPrivateStarted: PropTypes.bool,
  streamIds: PropTypes.array,
  fetchedLivestreamIds: PropTypes.array,
  replace: PropTypes.func,
  leaveLiveStreams: PropTypes.func,
  checkShouldDisplayKaraokeTooltip: PropTypes.func,
  setActiveStreamId: PropTypes.func,
  closeGameSicBo: PropTypes.func,
  preset: PropTypes.string,
};

LiveStreams.defaultProps = {
  usingGameId: null,
  streamId: null,
  staticContext: {},
  fetchUserDetailError: null,
  username: '',
  fetchUser: () => null,
  feedNameWithQuery: null,
  deviceHeight: 0,
  deviceWidth: 0,
  hasFeedFetched: false,
  nextPage: null,
  shouldUseInstantCommandTooltip: false,
  isGiftPanelOpen: false,
  isChatPanelOpen: false,
  isTriggerPrivatePanelOpen: false,
  isGamePanelOpen: false,
  isGameOpen: false,
  isChatroomScrolling: false,
  isPrivateStreamUnlocked: false,
  isStreamIdLiveStreaming: false,
  isExclusivePrivateRelatedToMe: false,
  isStreamIdHasKaraoke: false,
  hasGroupPrivateStarted: false,
  streamIds: [],
  fetchedLivestreamIds: [],
  replace: () => null,
  leaveLiveStreams: () => null,
  fetchFeeds: () => null,
  checkShouldDisplayKaraokeTooltip: () => null,
  setActiveStreamId: () => null,
  closeGameSicBo: () => null,
  preset: null,
};

const StyledLiveStreams = styled.div`
  width: 100vw;
  height: ${({ deviceHeight }) =>
    deviceHeight ? `${deviceHeight}px` : '100vh'};
  overflow: hidden;
`;

const LiveStreamWrapper = styled.div`
  overflow: hidden;
`;

const StyledSwiper = styled(Swiper)`
  .swiper-wrapper {
    display: flex;
    flex-direction: column;
  }
`;

const GameSicBoOpenStyle = css`
  position: fixed;
  bottom: 0px;
  left: 0px;
  z-index: ${sicBoZIndex};
  max-height: ${sicBoHeight +
  (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0)}px;
`;

const GameSicBoWrapper = styled.div`
  max-height: 0px;
  width: 100%;
  transition: max-height 0.6s ease-out;
  ${({ isOpen }) => (isOpen ? GameSicBoOpenStyle : '')};
`;

const GameDragonTigerFightOpenStyle = css`
  position: fixed;
  bottom: 0px;
  left: 0px;
  z-index: ${dragonTigerFightZIndex};
  max-height: ${dragonTigerFightHeight +
  dragonTigerFightHeaderHeight +
  (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0)}px;
`;

const GameDragonTigerFightWrapper = styled.div`
  max-height: 0px;
  width: 100%;
  transition: max-height 0.6s ease-out;
  ${({ isOpen }) => (isOpen ? GameDragonTigerFightOpenStyle : '')};
`;

const GameGiftSlotOpenStyle = css`
  position: fixed;
  bottom: 0px;
  left: 0px;
  z-index: ${giftSlotZIndex};
  max-height: ${giftSlotHeight +
  giftSlotHeaderHeight +
  (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0)}px;
`;

const GameGiftSlotWrapper = styled.div`
  max-height: 0px;
  width: 100%;
  transition: max-height 0.6s ease-out;
  ${({ isOpen }) => (isOpen ? GameGiftSlotOpenStyle : '')};
`;

const EnsureActiveStreamId = ({ activeStreamId = null }) => {
  const dispatch = useDispatch();
  const storeActiveStreamId = useSelector(state =>
    getOperationData(state, ['liveStream'], 'activeStreamId')
  );

  useEffect(() => {
    const timer = setInterval(() => {
      if (!!activeStreamId && activeStreamId !== storeActiveStreamId) {
        dispatch(setActiveStreamId({ activeStreamId }));
      }
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, [activeStreamId, dispatch, storeActiveStreamId]);
};

EnsureActiveStreamId.propTypes = {
  activeStreamId: PropTypes.string,
};

export default withRouter(LiveStreams);
